import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/site';

const FORM_ID = 'contact-feedback-form';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7the73hiaolq192ll0';

const guides = [
  {
    icon: 'ri-search-line',
    title: '搜索与筛选',
    desc: '在搜索页输入关键词可同时检索影视作品与资讯；分类页支持按题材、地区、年份与热度即时筛选。',
  },
  {
    icon: 'ri-book-open-line',
    title: '阅读剧情与影评',
    desc: '进入任意影视详情页，可查看完整剧情简介、影评摘要与演员阵容，并跳转演员资料页。',
  },
  {
    icon: 'ri-article-line',
    title: '浏览专题资讯',
    desc: '资讯专题页聚合了同一话题下的多篇报道与解读，点击资讯即可进入沉浸式阅读页面。',
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 姐姐的朋友影视内容站';
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMessage('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-primary-950">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 mt-4 mb-3">关于我们</h1>
          <p className="text-sm md:text-base text-background-200/80 max-w-2xl">
            {siteConfig.brand} 影视内容站，专注电影与电视剧的剧情介绍、影评资讯与演员资料。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2 flex flex-col gap-12">
            <section>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                网站介绍
              </h2>
              <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-loose">
                <p>
                  {siteConfig.brand} 影视内容站是一个面向影视剧爱好者的内容平台，围绕电影与电视剧提供详细的剧情介绍、
                  深度影评资讯与演员资料，尤其关注《姐姐的朋友》系列相关影视内容。
                </p>
                <p>
                  我们希望用户在决定观看一部作品之前，就能快速了解它的故事梗概、主创阵容与口碑评价，
                  也能在观看之后，找到更多关于人物与话题的解读。站内内容以清晰的层级组织，
                  便于长时间阅读与快速检索。
                </p>
                <p>
                  需要说明的是，本站为演示站点，所展示的影视作品、演员与资讯内容均为虚构演示素材，
                  不提供在线播放、下载或购票等任何实际服务。
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                使用说明
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {guides.map((item) => (
                  <div key={item.title} className="flex flex-col gap-3 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="flex items-center justify-center w-11 h-11 rounded-md bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="font-heading font-semibold text-base text-foreground-950">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">留言反馈</h2>
              <p className="text-sm text-foreground-500 mb-5">
                对站内内容有任何建议或想看的影视作品，欢迎给我们留言。
              </p>

              {status === 'success' ? (
                <div className="flex flex-col items-center text-center py-8">
                  <span className="flex items-center justify-center w-14 h-14 rounded-full bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-checkbox-circle-line text-3xl" />
                  </span>
                  <p className="text-sm font-medium text-foreground-900 mb-1">感谢您的反馈！</p>
                  <p className="text-xs text-foreground-500 mb-5">我们已收到您的留言，会认真阅读每一条建议。</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
                      昵称 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="请输入您的昵称"
                      className="px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="方便我们回复您的邮箱"
                      className="px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-700">
                      反馈类型
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="影视推荐">影视推荐</option>
                      <option value="资料纠错">资料纠错</option>
                      <option value="合作咨询">合作咨询</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
                      留言内容 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请填写您的建议或想看的作品（最多 500 字）"
                      className="px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 resize-none"
                    />
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-extra-note"
                  />

                  {status === 'error' && (
                    <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
                      {errorMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="flex items-center justify-center gap-2 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        提交中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        提交反馈
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}